import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddCircle, ContentCopy, Delete, Edit, MoreVert, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Divider, IconButton, LinearProgress, ListItemIcon, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useApiClient, useCopyToClipboard, useHasPermission, usePersistedTablePagination, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { AgentClient, AgentModel, AgentModelPagingResultModel } from '../../api/api';
import { Permissions } from '../../common/Permissions';
import AgentDialog from './AgentDialog';
import AgentAvatar from './AgentAvatar';

const pageSizeOptions = [20, 50, 100];

export default function AgentsListView() {
	const client = useApiClient(AgentClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const canCreateAgents = useHasPermission(Permissions.AgentCreate);
	const canUpdateAgents = useHasPermission(Permissions.AgentUpdate);
	const canDeleteAgents = useHasPermission(Permissions.AgentDelete);
	const { paginationModel, setPaginationModel } = usePersistedTablePagination('agents.pageSize', pageSizeOptions[1]);
	const [editingAgent, setEditingAgent] = useState<AgentModel | null>(null);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [deletingAgent, setDeletingAgent] = useState<AgentModel | null>(null);
	const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; agent: AgentModel } | null>(null);

	const { data, isLoading, isError, refetch } = useQuery<AgentModelPagingResultModel>({
		queryKey: ['agents', paginationModel.page, paginationModel.pageSize],
		queryFn: () => client.getAgents(paginationModel.page, paginationModel.pageSize, undefined),
	});

	const rows = data?.items ?? [];

	const deleteMutation = useMutation({
		mutationFn: (agent: AgentModel) => client.deleteAgent(agent.id),
		onSuccess: async () => {
			toastService.success('Agent deleted');
			setDeletingAgent(null);
			await queryClient.invalidateQueries({ queryKey: ['agents'] });
		},
		onError: (error) => toastService.error(`Agent could not be deleted: ${error instanceof Error ? error.message : 'Unknown error'}`),
	});

	const handleDialogSuccess = async () => {
		setIsCreateDialogOpen(false);
		setEditingAgent(null);
		await queryClient.invalidateQueries({ queryKey: ['agents'] });
	};

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Agents"
				subtitle="Manage workspace agents."
				utilityActions={<Tooltip title="Refresh agents"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>}
				primaryAction={canCreateAgents ? <Button variant="contained" startIcon={<AddCircle />} onClick={() => setIsCreateDialogOpen(true)}>Create agent</Button> : undefined}
				statistics={<GalleryStatistics items={[{ label: 'Agents on this page', value: rows.length }]} />}
			/>

			{isError && <Alert severity="error">Agents could not be loaded. Please refresh and try again.</Alert>}

			{isLoading && <LinearProgress aria-label="Loading agents" />}
			<Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
				{!isLoading && !isError && rows.length === 0 && <Alert severity="info">No agents on this page.</Alert>}
				{rows.length > 0 && (
					<Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
						<Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: 'minmax(0, 1fr) 180px 56px', px: 2.5, py: 1.4, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
							<Typography variant="subtitle2" color="text.secondary">Agent</Typography>
							<Typography variant="subtitle2" color="text.secondary">Type</Typography>
						</Box>
						<Box role="list" aria-label="Agents">
							{rows.map((agent, index) => (
								<Box key={agent.id}>
									<Box
										component="article"
										role="listitem"
										sx={theme => ({
											display: 'grid',
											gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) 180px 56px' },
											alignItems: 'center',
											columnGap: { xs: 1, md: 2 },
											py: 1.25,
											pl: 2,
											pr: 1.25,
											borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.85)}`,
											'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
										})}
									>
										<Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
											<AgentAvatar name={agent.name} fileId={agent.fileId} size={52} cutout />
											<Box sx={{ minWidth: 0 }}>
												<Typography component="h2" variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{agent.name}</Typography>
												<Typography variant="body2" color="text.secondary">{agent.isSystem ? 'System agent' : 'Workspace agent'}</Typography>
											</Box>
										</Stack>
										<Stack spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' } }}>
											<Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>AI Agent</Typography>
											<Chip size="small" variant="outlined" label={agent.isSystem ? 'System' : 'Workspace'} sx={{ width: 'fit-content', fontSize: 10, height: 20 }} />
										</Stack>
										<Tooltip title="Agent actions"><IconButton aria-label={`Actions for ${agent.name}`} aria-haspopup="menu" aria-expanded={actionMenu?.agent.id === agent.id || undefined} onClick={event => setActionMenu({ anchor: event.currentTarget, agent })} sx={{ bgcolor: 'action.hover' }}><MoreVert /></IconButton></Tooltip>
									</Box>
									{index < rows.length - 1 && <Divider />}
								</Box>
							))}
						</Box>
					</Paper>
				)}
			</Box>
			<Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1} flexWrap="wrap">
				<TextField select size="small" label="Agents per page" value={paginationModel.pageSize} onChange={event => setPaginationModel({ page: 0, pageSize: Number(event.target.value) })} sx={{ minWidth: 140 }}>
					{pageSizeOptions.map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}
				</TextField>
				<Button disabled={isLoading || paginationModel.page === 0} onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}>Previous</Button>
				<Typography variant="body2" aria-live="polite">Page {paginationModel.page + 1}</Typography>
				<Button disabled={isLoading || isError || !data?.hasNextPage} onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}>Next</Button>
			</Stack>
			<Menu anchorEl={actionMenu?.anchor} open={!!actionMenu} onClose={() => setActionMenu(null)}>
				<MenuItem onClick={() => { if (actionMenu) { copyToClipboard(actionMenu.agent.id, String); } setActionMenu(null); }}><ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>Copy ID</MenuItem>
				{actionMenu && !actionMenu.agent.isSystem && canUpdateAgents && <MenuItem onClick={() => { setEditingAgent(actionMenu.agent); setActionMenu(null); }}><ListItemIcon><Edit fontSize="small" /></ListItemIcon>Edit agent</MenuItem>}
				{actionMenu && !actionMenu.agent.isSystem && canDeleteAgents && <MenuItem sx={{ color: 'error.main' }} onClick={() => { setDeletingAgent(actionMenu.agent); setActionMenu(null); }}><ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>Delete agent</MenuItem>}
			</Menu>

			<AgentDialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} onSuccess={handleDialogSuccess} />
			<AgentDialog agent={editingAgent ?? undefined} open={editingAgent !== null} onClose={() => setEditingAgent(null)} onSuccess={handleDialogSuccess} />
			<ConfirmDialog
				open={deletingAgent !== null}
				title="Delete Agent"
				message={deletingAgent ? `Delete agent "${deletingAgent.name}"? This action cannot be undone.` : ''}
				confirmText="Delete"
				confirmColor="error"
				confirmDisabled={deleteMutation.isPending}
				cancelDisabled={deleteMutation.isPending}
				onConfirm={() => deletingAgent && deleteMutation.mutate(deletingAgent)}
				onCancel={() => setDeletingAgent(null)}
			/>
		</Stack>
	);
}
